import { useState, useEffect, useRef } from "react";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload, faXmark, faTrash,
  faFilePdf, faFileImage, faFileWord, faFileExcel, faFile,
} from "@fortawesome/free-solid-svg-icons";
import CrudApi from "@serie3/common/api/crudApi";
import downloadHandler from "@serie3/freelancesplaceapi/Domain/Shared/downloadHandler";
import DocumentsConfig from "@serie3/freelancesplaceapi/Config/Documents/DocumentsConfig";

const extIcon = (name = "") => {
  const ext = name.split(".").pop().toLowerCase();
  if (ext === "pdf") return { icon: faFilePdf, color: "#c62828", bg: "#ffebee" };
  if (["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext))
    return { icon: faFileImage, color: "#1565c0", bg: "#e3f2fd" };
  if (["doc", "docx"].includes(ext))
    return { icon: faFileWord, color: "#1a73e8", bg: "#e8f0fe" };
  if (["xls", "xlsx"].includes(ext))
    return { icon: faFileExcel, color: "#2e7d32", bg: "#e8f5e9" };
  return { icon: faFile, color: "#757575", bg: "#f5f5f5" };
};

const isImage = (name = "") =>
  ["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(name.split(".").pop().toLowerCase());

const isPdf = (name = "") =>
  name.split(".").pop().toLowerCase() === "pdf";

/**
 * attachment: { id, fileId, originalName }
 * onClose: () => void
 * onUnlink: (attachmentId) => void   — optional, shows a Remove button
 * endpointUnlink: string             — required when onUnlink is provided
 */
const FilePreviewModal = ({ attachment, onClose, onUnlink, endpointUnlink }) => {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const blobRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setUrl(null);
    setLoading(true);
    const load = async () => {
      const res = await CrudApi.getBinary(
        DocumentsConfig.endpoints.preview + attachment.fileId
      );
      if (cancelled) return;
      const blob = new Blob([res.data], {
        type: res.headers?.["content-type"] || "application/octet-stream",
      });
      const objectUrl = URL.createObjectURL(blob);
      blobRef.current = objectUrl;
      setUrl(objectUrl);
      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
      if (blobRef.current) { URL.revokeObjectURL(blobRef.current); blobRef.current = null; }
    };
  }, [attachment.fileId]);

  const handleUnlink = async () => {
    if (!onUnlink || !endpointUnlink) return;
    const res = await CrudApi.del(endpointUnlink, attachment.id);
    if (res.status === 200) {
      onUnlink(attachment.id);
      onClose();
    }
  };

  const { icon, color, bg } = extIcon(attachment.originalName);

  return (
    <>
      <style>{`
        .file-preview-modal-content {
          border-radius: 14px !important;
          overflow: hidden !important;
          border: none !important;
          box-shadow: 0 8px 40px rgba(0,0,0,0.2) !important;
        }
        @keyframes fpm-spin { to { transform: rotate(360deg); } }
      `}</style>
      <Modal
        show
        onHide={onClose}
        size="xl"
        dialogClassName="modal-90w"
        contentClassName="file-preview-modal-content"
      >
        <Modal.Header style={{
          background: "#fff", borderBottom: "1px solid #f0f0f0",
          padding: "16px 20px", display: "flex",
          alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
            <div style={{
              width: "32px", height: "32px", borderRadius: "8px",
              background: bg, display: "flex", alignItems: "center",
              justifyContent: "center", flexShrink: 0,
            }}>
              <FontAwesomeIcon icon={icon} style={{ color, fontSize: "0.85rem" }} />
            </div>
            <span style={{
              fontWeight: 700, fontSize: "0.95rem", color: "#212121",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {attachment.originalName}
            </span>
          </div>
          <button onClick={onClose} style={{
            background: "#f5f5f5", border: "none", borderRadius: "8px",
            width: "32px", height: "32px", display: "flex", alignItems: "center",
            justifyContent: "center", cursor: "pointer", color: "#757575",
          }}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </Modal.Header>

        {/* Preview area */}
        <Modal.Body style={{
          background: "#f8f9fb", padding: 0,
          height: "65vh", display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {loading ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px", color: "#9e9e9e" }}>
              <div style={{
                width: "32px", height: "32px", borderRadius: "50%",
                border: "3px solid #e8f0fe", borderTopColor: "#1a73e8",
                animation: "fpm-spin 0.8s linear infinite",
              }} />
              <span style={{ fontSize: "0.85rem" }}>Loading…</span>
            </div>
          ) : url && isPdf(attachment.originalName) ? (
            <iframe src={url} style={{ width: "100%", height: "100%", border: "none" }} title={attachment.originalName} />
          ) : url && isImage(attachment.originalName) ? (
            <img src={url} alt={attachment.originalName}
              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", padding: "20px" }} />
          ) : (
            <div style={{ textAlign: "center", color: "#9e9e9e", padding: "48px" }}>
              <FontAwesomeIcon icon={icon} style={{ fontSize: "3rem", color, marginBottom: "14px" }} />
              <div style={{ fontSize: "0.9rem" }}>No preview available for this file type.</div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer style={{
          background: "#fff", borderTop: "1px solid #f0f0f0",
          padding: "14px 20px", display: "flex", gap: "8px", justifyContent: "flex-end",
        }}>
          {onUnlink && (
            <button
              onClick={handleUnlink}
              style={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                padding: "8px 16px", background: "#ffebee", color: "#c62828",
                border: "none", borderRadius: "8px", fontWeight: 600,
                fontSize: "0.88rem", cursor: "pointer", marginRight: "auto",
              }}
            >
              <FontAwesomeIcon icon={faTrash} /> Remove
            </button>
          )}
          <button
            onClick={() => downloadHandler(
              DocumentsConfig.endpoints.preview + attachment.fileId,
              attachment.originalName
            )}
            style={{
              display: "inline-flex", alignItems: "center", gap: "6px",
              padding: "8px 16px", background: "#1a73e8", color: "#fff",
              border: "none", borderRadius: "8px", fontWeight: 600,
              fontSize: "0.88rem", cursor: "pointer",
            }}
          >
            <FontAwesomeIcon icon={faDownload} /> Download
          </button>
          <button onClick={onClose} style={{
            display: "inline-flex", alignItems: "center", gap: "6px",
            padding: "8px 16px", background: "#f5f5f5", color: "#424242",
            border: "none", borderRadius: "8px", fontWeight: 600,
            fontSize: "0.88rem", cursor: "pointer",
          }}>
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default FilePreviewModal;
